import { Component, computed, inject, input, output, signal } from '@angular/core';
import { ButtonComponent } from '@claas/claas-ui-components';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FileUploadState, fileUploadKey } from '../attachment.model';

function formatSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Prüft, ob eine Datei einem Typ-Token entspricht. Ein Token ist entweder ein
 * MIME-Typ (enthält `/`, ggf. mit Wildcard wie `image/*`) oder eine Datei-Endung
 * (mit oder ohne führenden Punkt).
 */
function matchesType(file: File, token: string): boolean {
  const normalized = token.trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  if (normalized.includes('/')) {
    const mime = file.type.toLowerCase();
    if (normalized.endsWith('/*')) {
      return mime.startsWith(normalized.slice(0, -1));
    }
    return mime === normalized;
  }
  const extension = normalized.startsWith('.') ? normalized : `.${normalized}`;
  return file.name.toLowerCase().endsWith(extension);
}

@Component({
  selector: 'sap-file-uploader',
  templateUrl: './uploader.html',
  styleUrls: ['./uploader.scss'],
  imports: [ButtonComponent, TranslatePipe],
})
export class FileUploader {
  public readonly allowedFileTypes = input<string[]>([]);
  public readonly forbiddenFileTypes = input<string[]>([]);
  /**
   * Gesamtgrenze in Bytes über **alle** ausgewählten Dateien. Bewusster „Glitch":
   * Der Wert stammt aus dem serverseitigen Pro-Datei-Limit ({@code maxFileSizeBytes}),
   * wird hier aber als Gesamt-Request-Limit interpretiert und durchgesetzt – siehe
   * {@link UploadConfig}. Bei Überschreitung wird der Upload-Button deaktiviert.
   */
  public readonly maxUploadSize = input<number | undefined>(undefined);
  /** Maximale Anzahl Dateien pro Request (Sicherheits-Limit aus der Config). */
  public readonly maxFiles = input<number | undefined>(undefined);
  public readonly disabled = input(false);
  // Defaults sind Übersetzungs-Keys; im Template via `translate`-Pipe aufgelöst.
  public readonly title = input('uploader.dropzone.title');
  public readonly subTitle = input('uploader.dropzone.subtitle');
  public readonly uploadStates = input<ReadonlyMap<string, FileUploadState>>(new Map());
  public readonly upload = output<File[]>();
  protected readonly files = signal<File[]>([]);
  protected readonly errors = signal<string[]>([]);
  protected readonly isDragover = signal(false);
  protected readonly hasFiles = computed(() => this.files().length > 0);
  protected readonly isCompleted = computed(() => {
    const states = this.uploadStates();
    return (
      this.hasFiles() &&
      this.files().every((file) => {
        const status = states.get(fileUploadKey(file))?.status;
        return status === 'success' || status === 'error';
      })
    );
  });
  protected readonly totalSize = computed(() =>
    this.files().reduce((sum, file) => sum + file.size, 0),
  );
  protected readonly remainingSize = computed(() => {
    const limit = this.maxUploadSize();
    return limit === undefined ? undefined : limit - this.totalSize();
  });
  protected readonly exceedsLimit = computed(() => {
    const remaining = this.remainingSize();
    return remaining !== undefined && remaining < 0;
  });
  protected readonly fileCount = computed(() => this.files().length);
  protected readonly exceedsFileCount = computed(() => {
    const limit = this.maxFiles();
    return limit !== undefined && this.fileCount() > limit;
  });
  protected readonly canUpload = computed(
    () => this.hasFiles() && !this.disabled() && !this.exceedsLimit() && !this.exceedsFileCount(),
  );
  protected readonly acceptAttr = computed(() => this.allowedFileTypes().join(','));
  protected readonly formatSize = formatSize;

  private readonly translate = inject(TranslateService);

  protected stateFor(file: File): FileUploadState | undefined {
    return this.uploadStates().get(fileUploadKey(file));
  }

  protected handleInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.addFiles(target.files);
    target.value = '';
  }

  protected handleDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled()) {
      return;
    }
    this.isDragover.set(true);
  }

  protected handleDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragover.set(false);
  }

  protected handleDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragover.set(false);
    if (this.disabled()) {
      return;
    }
    this.addFiles(event.dataTransfer?.files ?? null);
  }

  protected removeFile(index: number): void {
    this.files.update((current) => current.filter((_, i) => i !== index));
  }

  protected clear(): void {
    this.files.set([]);
    this.errors.set([]);
  }

  protected submit(): void {
    if (!this.canUpload()) {
      return;
    }
    this.upload.emit(this.files());
  }

  private addFiles(fileList: FileList | null): void {
    if (!fileList || fileList.length === 0) {
      return;
    }
    const allowed = this.allowedFileTypes();
    const forbidden = this.forbiddenFileTypes();
    const newErrors: string[] = [];
    const accepted: File[] = [];
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList.item(i);
      if (!file) {
        continue;
      }
      // Deny-Liste gewinnt immer.
      if (forbidden.some((token) => matchesType(file, token))) {
        newErrors.push(this.translate.instant('errors.fileTypeNotAllowed', { name: file.name }));
        continue;
      }
      // Leere Allow-Liste ⇒ alles erlaubt (außer Verbotenem).
      const acceptsType = allowed.length === 0 || allowed.some((token) => matchesType(file, token));
      if (!acceptsType) {
        newErrors.push(this.translate.instant('errors.fileTypeNotAllowed', { name: file.name }));
        continue;
      }
      // Bewusst keine Pro-Datei-Größenprüfung: Das Größenlimit wird als
      // Gesamtgrenze über alle Dateien durchgesetzt (siehe maxUploadSize).
      accepted.push(file);
    }
    if (accepted.length > 0) {
      this.files.update((current) => {
        const seen = new Set(current.map(fileUploadKey));
        const result = [...current];
        for (const file of accepted) {
          const key = fileUploadKey(file);
          if (!seen.has(key)) {
            result.push(file);
            seen.add(key);
          }
        }
        return result;
      });
    }
    this.errors.set(newErrors);
  }
}
